import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { PERMISSIONS } from 'src/config/roleConfig'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown'
import type { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { InfoTooltip } from 'src/components/Reusable/Tooltip/InfoTooltip'
import { TooltipPositions } from 'src/components/Reusable/Tooltip/Tooltip'
import { Spinner } from 'src/Throbber'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import {
  ErrorText,
  FormBody,
  Input,
  Label,
  Row,
  Textarea,
} from './AddWorkflowEvidenceForm.styles'

export type Option = {
  name: string
  pid: string
  pname: string
  tid: string
  tname: string
  tpid: string
  receptorId?: string
  receptorDisplayName?: string
}

export const NO_PRESET_VALUE = '__no_preset__'

export type WorkflowFormState = {
  receptorId: string
  project: string
  taskType: string
  taskName: string
  title: string
  description: string
  assigneeId: string
  useNoPreset: boolean
  saveAsPreset: boolean
  siteUrl: string
  label: string
}

export type AddWorkflowEvidenceFormProps = {
  modalId: ModalFormId
  isShown: boolean
  onSubmitFunc: (data: WorkflowFormState) => Promise<void> | void
  onStateChange?: (data: WorkflowFormState) => void
  configs?: Option[]
  siteUrl: string
  label: string
  controlId: string
  description: string
  initialTitle?: string
  name?: string
  receptorDisplayName?: string
  showTicketDetails?: boolean
  ticketDetailsLabel?: string
  assigneeItems?: SelectItem[]
  assigneeLoading?: boolean
  requireAssignee?: boolean
  ticketError?: string
  noPresetMode?: boolean
  noPresetInstanceItems?: SelectItem[]
  noPresetInstanceDefaultValues?: SelectItem[]
  onSelectNoPresetInstance?: (selected: SelectItem[]) => void
  noPresetProjectItems?: SelectItem[]
  noPresetProjectDefaultValues?: SelectItem[]
  onSelectNoPresetProject?: (selected: SelectItem[]) => void
  noPresetTaskTypeItems?: SelectItem[]
  noPresetTaskTypeDefaultValues?: SelectItem[]
  onSelectNoPresetTaskType?: (selected: SelectItem[]) => void
  preselectedReceptorId?: string
}

export const AddWorkflowEvidenceForm: React.FC<
  AddWorkflowEvidenceFormProps
> = ({
  modalId,
  isShown,
  onSubmitFunc,
  onStateChange,
  configs = [],
  siteUrl = '',
  label = '',
  controlId = '',
  description = '',
  initialTitle = '',
  name = '',
  receptorDisplayName = '',
  showTicketDetails = false,
  ticketDetailsLabel = '',
  assigneeItems = [],
  assigneeLoading = false,
  requireAssignee = false,
  ticketError = '',
  noPresetMode = false,
  noPresetInstanceItems = [],
  noPresetInstanceDefaultValues = [],
  onSelectNoPresetInstance,
  noPresetProjectItems = [],
  noPresetProjectDefaultValues = [],
  onSelectNoPresetProject,
  noPresetTaskTypeItems = [],
  noPresetTaskTypeDefaultValues = [],
  onSelectNoPresetTaskType,
  preselectedReceptorId = '',
}) => {
  const workflowDropdownPermissions = [PERMISSIONS.AUDITOR_ACTION]

  const makeInitial = useCallback(
    (
      nextSiteUrl?: string,
      nextLabel?: string,
      nextControlId?: string,
    ): WorkflowFormState => ({
      receptorId: '',
      project: '',
      taskType: '',
      taskName: '',
      title:
        initialTitle?.trim() ||
        (nextControlId
          ? `[${nextControlId}] Request for evidence`
          : 'Request for evidence'),
      description: description || '',
      assigneeId: '',
      useNoPreset: false,
      saveAsPreset: false,
      siteUrl: nextSiteUrl || '',
      label: nextLabel || '',
    }),
    [description, initialTitle],
  )

  const [state, setState] = useState<WorkflowFormState>(() =>
    makeInitial(siteUrl, label, controlId),
  )
  const hasInitializedForOpenRef = useRef(false)
  const hasUserSelectionInteractionRef = useRef(false)

  const selectedIndex = useMemo(
    () =>
      configs.findIndex(
        (c) =>
          c.pid === state.project &&
          c.tid === state.taskType &&
          (c.receptorId ?? '') === state.receptorId,
      ),
    [configs, state.project, state.taskType, state.receptorId],
  )

  const applyConfigOption = useCallback(
    (sel: Option, baseState?: WorkflowFormState) => {
      if (sel.receptorId === NO_PRESET_VALUE) {
        const onlyInstanceId =
          noPresetInstanceItems.length === 1
            ? String(noPresetInstanceItems[0]?.value ?? '')
            : String(
                noPresetInstanceDefaultValues[0]?.value ??
                  preselectedReceptorId,
              )
        setState((s) => ({
          ...(baseState ?? s),
          useNoPreset: true,
          receptorId: onlyInstanceId,
          project: '',
          taskType: '',
          taskName: '',
          assigneeId: '',
          saveAsPreset: false,
        }))
        return
      }
      setState((s) => ({
        ...(baseState ?? s),
        useNoPreset: false,
        receptorId: sel.receptorId || '',
        project: sel.pid,
        taskType: sel.tid,
        taskName: sel.tname,
        assigneeId: '',
        saveAsPreset: false,
      }))
    },
    [
      noPresetInstanceItems,
      noPresetInstanceDefaultValues,
      preselectedReceptorId,
    ],
  )

  useEffect(() => {
    if (!isShown) {
      hasInitializedForOpenRef.current = false
      hasUserSelectionInteractionRef.current = false
      return
    }
    if (configs.length === 0) return
    if (hasUserSelectionInteractionRef.current) return

    const selectedReceptorId = preselectedReceptorId.trim()
    const presetForSelectedReceptor = selectedReceptorId
      ? configs.find(
          (c) =>
            (c.receptorId ?? '') === selectedReceptorId &&
            c.receptorId !== NO_PRESET_VALUE,
        )
      : undefined
    const next = makeInitial(siteUrl, label, controlId)

    if (presetForSelectedReceptor) {
      applyConfigOption(presetForSelectedReceptor, next)
      hasInitializedForOpenRef.current = true
      return
    }

    if (selectedReceptorId) {
      hasInitializedForOpenRef.current = true
      setState({
        ...next,
        useNoPreset: true,
        saveAsPreset: false,
        receptorId: selectedReceptorId,
        project: '',
        taskType: '',
        taskName: '',
        assigneeId: '',
      })
      hasInitializedForOpenRef.current = true
      return
    }

    if (hasInitializedForOpenRef.current) return

    const firstPreset = configs.find((c) => c.receptorId !== NO_PRESET_VALUE)
    if (firstPreset) {
      applyConfigOption(firstPreset, next)
      hasInitializedForOpenRef.current = true
      return
    }

    setState(next)
    hasInitializedForOpenRef.current = true
  }, [
    isShown,
    siteUrl,
    label,
    controlId,
    description,
    initialTitle,
    makeInitial,
    configs,
    preselectedReceptorId,
    applyConfigOption,
  ])

  useEffect(() => {
    if (state.useNoPreset) return
    if (selectedIndex < 0) {
      setState((s) => ({
        ...s,
        receptorId: '',
        project: '',
        taskType: '',
        taskName: '',
        useNoPreset: false,
        saveAsPreset: false,
      }))
    }
  }, [selectedIndex, state.useNoPreset])

  useEffect(() => {
    if (
      !showTicketDetails ||
      !requireAssignee ||
      state.assigneeId ||
      assigneeItems.length === 0
    ) {
      return
    }
    setState((s) => ({
      ...s,
      assigneeId: String(assigneeItems[0].value ?? ''),
    }))
  }, [showTicketDetails, requireAssignee, state.assigneeId, assigneeItems])

  useEffect(() => {
    if (!isShown) return
    onStateChange?.(state)
  }, [isShown, state, onStateChange])

  const getConfigLabel = useCallback(
    (option: Option): string => {
      if (option.receptorId === NO_PRESET_VALUE) {
        return 'No Preset'
      }
      const receptorName =
        option.receptorDisplayName?.trim() ||
        receptorDisplayName.trim() ||
        name.trim()
      const receptorPrefix = receptorName ? `${receptorName} - ` : ''
      return `${option.name} (${receptorPrefix}${option.pname} - ${option.tname})`
    },
    [name, receptorDisplayName],
  )

  const configItems: SelectItem[] = useMemo(
    () =>
      configs.map((c, i) => ({
        name: getConfigLabel(c),
        value: i,
      })),
    [configs, getConfigLabel],
  )

  const configDefaultValues: SelectItem[] = useMemo(() => {
    if (selectedIndex < 0 || !configs[selectedIndex]) return []
    const c = configs[selectedIndex]
    return [{ name: getConfigLabel(c), value: selectedIndex }]
  }, [configs, getConfigLabel, selectedIndex])

  const selectedConfig = selectedIndex >= 0 ? configs[selectedIndex] : undefined
  const selectedNoPreset = selectedConfig?.receptorId === NO_PRESET_VALUE
  const effectiveNoPresetMode = noPresetMode || selectedNoPreset

  useEffect(() => {
    if (!effectiveNoPresetMode) return
    if (state.receptorId) return
    if (noPresetInstanceItems.length !== 1) return

    const onlyInstanceId = String(noPresetInstanceItems[0]?.value ?? '')
    if (!onlyInstanceId) return

    setState((s) => ({
      ...s,
      useNoPreset: true,
      receptorId: onlyInstanceId,
    }))
  }, [effectiveNoPresetMode, state.receptorId, noPresetInstanceItems])

  const assigneeDefaultValues: SelectItem[] = useMemo(() => {
    if (!state.assigneeId) return []
    const selected = assigneeItems.find(
      (item) => String(item.value) === state.assigneeId,
    )
    return selected ? [selected] : []
  }, [state.assigneeId, assigneeItems])

  const handleConfigSelect = useCallback(
    (selected: SelectItem[]) => {
      hasUserSelectionInteractionRef.current = true
      const item = selected[0]
      if (item == null) return
      const idx = Number(item.value)
      const sel = configs[idx]
      if (!sel) return
      applyConfigOption(sel)
    },
    [configs, applyConfigOption],
  )

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (showTicketDetails && requireAssignee && !state.assigneeId?.trim())
      return
    if (!state.receptorId || !state.project || !state.title?.trim()) return
    await onSubmitFunc(state)
  }

  return (
    <FormBody id={modalId} onSubmit={onSubmit}>
      {configs.length > 0 ? (
        <>
          <Row>
            <Label>
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Preset
                <InfoTooltip
                  id="workflow-preset-tooltip"
                  placement={TooltipPositions.right}
                  body="Workflow presets come from your receptor configuration."
                  noMargin
                />
              </span>
            </Label>
            <SelectDropdown
              label=""
              items={configItems}
              defaultValues={configDefaultValues}
              requiredPermissions={workflowDropdownPermissions}
              onSelectCb={handleConfigSelect}
              placeholder="Select preset to fill in task information (optional)"
              hideError
            />
          </Row>
          {showTicketDetails ? (
            <>
              {effectiveNoPresetMode ? (
                <>
                  <Row>
                    <Label>Instance</Label>
                    <SelectDropdown
                      label=""
                      items={noPresetInstanceItems}
                      defaultValues={noPresetInstanceDefaultValues}
                      requiredPermissions={workflowDropdownPermissions}
                      onSelectCb={(selected: SelectItem[]) => {
                        hasUserSelectionInteractionRef.current = true
                        const item = selected?.[0]
                        const receptorId = item ? String(item.value ?? '') : ''
                        setState((s) => ({
                          ...s,
                          useNoPreset: true,
                          receptorId,
                          project: '',
                          taskType: '',
                          taskName: '',
                          assigneeId: '',
                          saveAsPreset: s.saveAsPreset,
                        }))
                        onSelectNoPresetInstance?.(selected)
                      }}
                      placeholder="Select instance"
                      hideError
                    />
                  </Row>
                  <Row>
                    <Label>Project</Label>
                    <SelectDropdown
                      label=""
                      items={noPresetProjectItems}
                      defaultValues={noPresetProjectDefaultValues}
                      requiredPermissions={workflowDropdownPermissions}
                      onSelectCb={(selected: SelectItem[]) => {
                        hasUserSelectionInteractionRef.current = true
                        const item = selected?.[0]
                        const projectId = item ? String(item.value ?? '') : ''
                        setState((s) => ({
                          ...s,
                          useNoPreset: true,
                          project: projectId,
                          taskType: '',
                          taskName: '',
                          assigneeId: '',
                          saveAsPreset: s.saveAsPreset,
                        }))
                        onSelectNoPresetProject?.(selected)
                      }}
                      placeholder="Select project"
                      hideError
                    />
                  </Row>
                  <Row>
                    <Label>Task Type</Label>
                    <SelectDropdown
                      label=""
                      items={noPresetTaskTypeItems}
                      defaultValues={noPresetTaskTypeDefaultValues}
                      requiredPermissions={workflowDropdownPermissions}
                      onSelectCb={(selected: SelectItem[]) => {
                        hasUserSelectionInteractionRef.current = true
                        const item = selected?.[0]
                        const taskTypeId = item ? String(item.value ?? '') : ''
                        const taskTypeName = item ? String(item.name ?? '') : ''
                        setState((s) => ({
                          ...s,
                          useNoPreset: true,
                          taskType: taskTypeId,
                          taskName: taskTypeName,
                          assigneeId: '',
                          saveAsPreset: s.saveAsPreset,
                        }))
                        onSelectNoPresetTaskType?.(selected)
                      }}
                      placeholder="Select task type"
                      hideError
                    />
                  </Row>
                </>
              ) : (
                <>
                  <Row>
                    <Label htmlFor="workflow-ticket-project">Project</Label>
                    <Input
                      id="workflow-ticket-project"
                      value={selectedConfig?.pname || state.project}
                      readOnly
                    />
                  </Row>
                  <Row>
                    <Label htmlFor="workflow-ticket-task-type">Task Type</Label>
                    <Input
                      id="workflow-ticket-task-type"
                      value={selectedConfig?.tname || state.taskType}
                      readOnly
                    />
                  </Row>
                </>
              )}
              <Row>
                <Label htmlFor="workflow-ticket-title">Title</Label>
                <Input
                  id="workflow-ticket-title"
                  value={state.title}
                  onChange={(e) =>
                    setState((s) => ({ ...s, title: e.target.value }))
                  }
                  placeholder="Request for Evidence"
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-description">Description</Label>
                <Textarea
                  id="workflow-ticket-description"
                  value={state.description}
                  onChange={(e) =>
                    setState((s) => ({ ...s, description: e.target.value }))
                  }
                  placeholder="Required evidence details"
                />
              </Row>
              <Row>
                <Label>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    Assignee
                    {assigneeLoading ? (
                      <Spinner size="s" color="primary" />
                    ) : null}
                  </span>
                </Label>
                <SelectDropdown
                  label=""
                  items={assigneeItems}
                  defaultValues={assigneeDefaultValues}
                  requiredPermissions={workflowDropdownPermissions}
                  onSelectCb={(selected: SelectItem[]) => {
                    const item = selected?.[0]
                    if (!item) return
                    setState((s) => ({
                      ...s,
                      assigneeId: String(item.value ?? ''),
                    }))
                  }}
                  placeholder="Select assignee"
                  hideError
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-label">Label</Label>
                <Input
                  id="workflow-ticket-label"
                  value={ticketDetailsLabel}
                  readOnly
                />
              </Row>
              {effectiveNoPresetMode ? (
                <Row>
                  <div />
                  <CheckBoxInput
                    id="workflow-save-as-preset"
                    label="Save as preset"
                    checked={state.saveAsPreset}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        saveAsPreset: e.currentTarget.checked,
                      }))
                    }
                  />
                </Row>
              ) : null}
              {ticketError ? <ErrorText>{ticketError}</ErrorText> : null}
            </>
          ) : null}
        </>
      ) : noPresetMode ? (
        <>
          <Row>
            <Label>
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Preset
                <InfoTooltip
                  id="workflow-preset-tooltip-empty"
                  placement={TooltipPositions.right}
                  body="Workflow presets come from your receptor configuration."
                  noMargin
                />
              </span>
            </Label>
            <SelectDropdown
              label=""
              items={configItems}
              defaultValues={configDefaultValues}
              requiredPermissions={workflowDropdownPermissions}
              onSelectCb={handleConfigSelect}
              placeholder="Select preset to fill in task information (optional)"
              hideError
            />
          </Row>
          <Row>
            <Label>Instance</Label>
            <SelectDropdown
              label=""
              items={noPresetInstanceItems}
              defaultValues={noPresetInstanceDefaultValues}
              requiredPermissions={workflowDropdownPermissions}
              onSelectCb={(selected: SelectItem[]) => {
                hasUserSelectionInteractionRef.current = true
                const item = selected?.[0]
                const receptorId = item ? String(item.value ?? '') : ''
                setState((s) => ({
                  ...s,
                  useNoPreset: true,
                  receptorId,
                  project: '',
                  taskType: '',
                  taskName: '',
                  assigneeId: '',
                  saveAsPreset: s.saveAsPreset,
                }))
                onSelectNoPresetInstance?.(selected)
              }}
              placeholder="Select instance"
              hideError
            />
          </Row>
          <Row>
            <Label>Project</Label>
            <SelectDropdown
              label=""
              items={noPresetProjectItems}
              defaultValues={noPresetProjectDefaultValues}
              requiredPermissions={workflowDropdownPermissions}
              onSelectCb={(selected: SelectItem[]) => {
                hasUserSelectionInteractionRef.current = true
                const item = selected?.[0]
                const projectId = item ? String(item.value ?? '') : ''
                setState((s) => ({
                  ...s,
                  useNoPreset: true,
                  project: projectId,
                  taskType: '',
                  taskName: '',
                  assigneeId: '',
                  saveAsPreset: s.saveAsPreset,
                }))
                onSelectNoPresetProject?.(selected)
              }}
              placeholder="Select project"
              hideError
            />
          </Row>
          <Row>
            <Label>Task Type</Label>
            <SelectDropdown
              label=""
              items={noPresetTaskTypeItems}
              defaultValues={noPresetTaskTypeDefaultValues}
              requiredPermissions={workflowDropdownPermissions}
              onSelectCb={(selected: SelectItem[]) => {
                hasUserSelectionInteractionRef.current = true
                const item = selected?.[0]
                const taskTypeId = item ? String(item.value ?? '') : ''
                const taskTypeName = item ? String(item.name ?? '') : ''
                setState((s) => ({
                  ...s,
                  useNoPreset: true,
                  taskType: taskTypeId,
                  taskName: taskTypeName,
                  assigneeId: '',
                  saveAsPreset: s.saveAsPreset,
                }))
                onSelectNoPresetTaskType?.(selected)
              }}
              placeholder="Select task type"
              hideError
            />
          </Row>
          {showTicketDetails ? (
            <>
              <Row>
                <Label htmlFor="workflow-ticket-title">Title</Label>
                <Input
                  id="workflow-ticket-title"
                  value={state.title}
                  onChange={(e) =>
                    setState((s) => ({ ...s, title: e.target.value }))
                  }
                  placeholder="Request for Evidence"
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-description">Description</Label>
                <Textarea
                  id="workflow-ticket-description"
                  value={state.description}
                  onChange={(e) =>
                    setState((s) => ({ ...s, description: e.target.value }))
                  }
                  placeholder="Required evidence details"
                />
              </Row>
              <Row>
                <Label>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    Assignee
                    {assigneeLoading ? (
                      <Spinner size="s" color="primary" />
                    ) : null}
                  </span>
                </Label>
                <SelectDropdown
                  label=""
                  items={assigneeItems}
                  defaultValues={assigneeDefaultValues}
                  requiredPermissions={workflowDropdownPermissions}
                  onSelectCb={(selected: SelectItem[]) => {
                    const item = selected?.[0]
                    if (!item) return
                    setState((s) => ({
                      ...s,
                      assigneeId: String(item.value ?? ''),
                    }))
                  }}
                  placeholder="Select assignee"
                  hideError
                />
              </Row>
              <Row>
                <Label htmlFor="workflow-ticket-label">Label</Label>
                <Input
                  id="workflow-ticket-label"
                  value={ticketDetailsLabel}
                  readOnly
                />
              </Row>
              <Row>
                <div />
                <CheckBoxInput
                  id="workflow-save-as-preset-empty"
                  label="Save as preset"
                  checked={state.saveAsPreset}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      saveAsPreset: e.currentTarget.checked,
                    }))
                  }
                />
              </Row>
              {ticketError ? <ErrorText>{ticketError}</ErrorText> : null}
            </>
          ) : null}
        </>
      ) : (
        <></>
      )}
    </FormBody>
  )
}
